import {
  ITransaction,
  TransactionStatus,
  TransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { WSCategory } from "@wingspanhq/bookkeeping/dist/lib/interfaces/transaction";
import {
  toWSDate,
  toWSDateString,
  WSFilters,
  WSInfiniteScroll,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSFilterDateRangeOption } from "@wingspanhq/fe-component-library/dist/lib/components/WSFilters/types";
import { WSTableRowMenuAction } from "@wingspanhq/fe-component-library/dist/lib/components/WSTable/types";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { flatten } from "lodash";
import React, { useMemo } from "react";
import { Route, Switch } from "react-router-dom";
import {
  getTransactionAvatarProps,
  getTransactionName
} from "../../../Bookkeeping/utils";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useQueryListInternalAccountTransactions } from "../../../query/banking/queries/useQueryListInternalAccountTransactions";
import {
  TransactionParams,
  useQueryTransactions,
  useQueryTransactionsSummary
} from "../../../query/bookkeeping/queries";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  useQueryAccounts,
  useQueryMemberProfile
} from "../../../query/users/queries";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { useUrlQueryFilters } from "../../../utils/router";
import { CashbackCell } from "../components/CashbackCell";
import { TablePendingTransactions } from "../components/TablePendingTransactions";
import { useCards } from "../hooks/useCards";
import { selectorTransactionCashbackAmount } from "../selectors/selectorTransactionCashbackAmount";
import { disputeTransaction } from "../utils/disputeTransaction";
import { WalletTransaction } from "./WalletTransaction";

const dateRangeMap = {
  all: "ALL",
  custom: "CUSTOM",
  pastWeek: "PAST_WEEK",
  pastMonth: "PAST_MONTH",
  yearToDate: "PAST_YEAR"
} as const;

type FiltersType = {
  dateRange?: WSFilterDateRangeOption;
  customDueDateRange?: string[];
};

type TableItem = {
  source: ITransaction;
  merchant: string;
  date: Date;
  type: TransactionType;
  amount: number;
  receipt?: string;
  business: boolean;
  category?: WSCategory;
  subcategory?: string | boolean | null;
  account?: string;
};

export const defaultWalletFilters: FiltersType & { search?: string } = {
  // dateRange: "all",
  // customDueDateRange: [],
  // search: ""
};

export const RouteTransactions: React.FC = () => {
  const {
    defaultFilters,
    filters,
    changedFilters,
    setFilters,
    redirectWithFilters
  } = useUrlQueryFilters(defaultWalletFilters);

  const userId = useUserId();
  const qMember = useQueryMemberProfile(userId);

  const { search, ...params } = filters;

  const queryInternalAccounts = useQueryInternalAccounts();
  const bankingAccount = queryInternalAccounts.data?.find(
    selectorActiveBankingAccount
  );
  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    bankingAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!bankingAccount
    }
  );
  const queryAccounts = useQueryAccounts();

  const queryFeatureFlags = useFeatureFlags();
  const featureFlags = queryFeatureFlags.data;

  const columns: Array<WSTableColumn<TableItem>> = [
    {
      config: {
        header: { text: "Merchant" },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowData => (
        <WSTableCell
          avatar={getTransactionAvatarProps(rowData.data.source)}
          text={rowData.data.merchant}
          pill={rowData.data.business ? undefined : { text: "Ignored" }}
          secondaryTextColor={
            rowData.data.business &&
            rowData.data.type !== TransactionType.Transfer
              ? "gray600"
              : "gray400"
          }
          secondaryText={toWSDateString(rowData.data.date, "monthDayYear")}
        />
      )
    },
    {
      config: {
        hideOnScreens: ["XS", "S", "M"],
        header: { text: "Description" },
        gridTemplateSizeMax: "0.5fr"
        // justify: "end"
      },
      renderFunction: rowData => (
        <WSTableCell
          text={
            rowData.data.category
              ? `${rowData.data.category}${
                  rowData.data.subcategory
                    ? ` - ${rowData.data.subcategory}`
                    : ""
                }`
              : rowData.data.type
          }
        />
      )
    },
    /*
    {
      config: {
        hideOnScreens: ["XS"],
        header: { text: "Account" },
        gridTemplateSizeMax: "0.2fr"
      },
      renderFunction: rowData => (
        <WSTableCell
          text={rowData.data.account ? `•••${rowData.data.account}` : "--"}
        />
      )
    },
    */
    {
      config: {
        hideOnScreens: featureFlags?.cashback2025
          ? ["XS"]
          : ["XS", "S", "M", "L", "XL"],
        header: { text: "Cashback Amount" },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowData => {
        const transaction = rowData.data.source;

        const cashbackAmount = selectorTransactionCashbackAmount(transaction);

        if (cashbackAmount) {
          return (
            <CashbackCell
              amount={cashbackAmount}
              currency={transaction.currency}
            />
          );
        }
        return <WSTableCell text={undefined} />;
      }
    },
    {
      config: {
        justify: "end",
        header: { text: "Amount" },
        gridTemplateSizeMax: "auto"
      },
      renderFunction: rowData => (
        <WSTableCell
          text={formatMoney(
            rowData.data.source.amount * -1,
            rowData.data.source.currency,
            {
              showSign: true
            }
          )}
        />
      )
    }
  ];

  const requestParams: TransactionParams = {
    // `date` is normalized to noon UTC, so it renders as the same day in every
    // timezone and drifts from the payroll run date, which is a raw instant
    // shown in local time. Read `createdAt` instead so the wallet and payroll
    // views agree on the day, and keep sort + filter on the same field.
    sort: {
      createdAt: "desc"
    },
    dateField: "createdAt",
    dateRange: dateRangeMap[params.dateRange as keyof typeof dateRangeMap],
    category: "ALL",
    customDateRange: params.customDueDateRange as any as Date[],
    pageSize: 10,
    status: [TransactionStatus.Active],
    type: [
      TransactionType.Reward,
      TransactionType.Expense,
      TransactionType.Income,
      TransactionType.Transfer,
      TransactionType.ATMTransaction,
      TransactionType.Unknown
    ],
    nonBusiness: true,
    businessBanking: true
  };

  const queryTransactionsSummary = useQueryTransactionsSummary(requestParams);
  const queryTransactions = useQueryTransactions(requestParams);
  const cards = useCards();
  const accounts = queryAccounts.data || [];
  const transactionsList = flatten(queryTransactions.data?.pages || []);
  const pendingTransactions = useMemo(
    () =>
      (queryPendingTransactions.data?.pages || []).flatMap(
        page => page.transactions
      ),
    [queryPendingTransactions.data?.pages]
  );
  const transactionsListMapped: WSTableItem<TableItem>[] = transactionsList.map(
    transaction => {
      const card = cards.find(
        card => card.cardId === transaction.wingspanCardId
      );

      const account = accounts.find(
        account => account.accountId === transaction.accountId
      );

      return {
        data: {
          source: transaction,
          merchant: getTransactionName(transaction),
          date: transaction.createdAt,
          type: transaction.type,
          amount: transaction.amount,
          receipt: transaction.receiptFileId,
          business: true,
          category: transaction.wsCategory,
          subcategory: transaction.labels.subcategory,
          account: card?.last4Digits || account?.mask
        },
        id: transaction.transactionId
      };
    }
  );

  const resultCount =
    pendingTransactions.length + (queryTransactionsSummary.data?.listSize || 0);

  return (
    <>
      <Switch>
        <Route
          path="/member/wallet/transactions/:transactionId"
          component={WalletTransaction}
        />
      </Switch>

      <WSFilters<FiltersType>
        my="L"
        values={params}
        onFilter={(newValues: any) => {
          setFilters(
            typeof newValues.dateRange === "string"
              ? {
                  search,
                  dateRange: newValues.dateRange,
                  customDueDateRange: []
                }
              : newValues.dateRange
                ? {
                    search,
                    dateRange: "custom",
                    customDueDateRange: [
                      newValues.dateRange.start,
                      newValues.dateRange.end
                    ]
                  }
                : {}
          );
        }}
        info={{
          count: resultCount
        }}
        primaryFilters={[
          {
            name: "dateRange",
            nonExpandable: true,
            type: "daterange",
            options: ["all", "pastWeek", "pastMonth", "yearToDate", "custom"]
          }
        ]}
      />

      <WSTable
        showHeaderForEmpty={!!resultCount}
        showHeader={!!resultCount}
        my="XL"
        columns={[
          ...columns,
          {
            config: {
              header: { text: "" },
              gridTemplateSizeMax: "72px"
            },
            renderFunction: () => ""
          }
        ]}
        tableData={[]}
      />

      {Object.keys(changedFilters).length ? null : (
        <TablePendingTransactions
          accounts={accounts}
          pendingTransactions={pendingTransactions}
        />
      )}

      <WSInfiniteScroll
        onLoad={() => {
          queryTransactions.fetchNextPage();
        }}
        loadMore={transactionsList.length > 0}
        endOfList={!queryTransactions.hasNextPage}
        loading={!!queryTransactions.isFetchingNextPage}
      >
        <WSTable<TableItem>
          showHeader={false}
          forceShowGroups
          my="XL"
          loading={
            queryTransactions.isInitialLoading ||
            queryPendingTransactions.isInitialLoading
          }
          columns={columns}
          onRowClick={item => {
            redirectWithFilters(
              `/member/wallet/transactions/${item.data.source.transactionId}`
            );
          }}
          getGroupName={item => {
            const year = new Date(item.data.date).getFullYear();
            const month = toWSDate(item.data.date, "month").toString();

            return `${month} ${year}`;
          }}
          rowMenuActions={item => {
            return [
              {
                label: "View details",
                icon: "view",
                onClick: () => {
                  redirectWithFilters(
                    `/member/wallet/transactions/${item.data.source.transactionId}`
                  );
                }
              },
              {
                separator: true
              },
              {
                label: "Dispute transaction",
                icon: "dispute",
                onClick: () => {
                  qMember.data &&
                    disputeTransaction(
                      item.data.source,
                      qMember.data,
                      item.data.account
                    );
                }
              }
            ] as WSTableRowMenuAction<TableItem>[];
          }}
          tableData={transactionsListMapped}
          bottomMessage={
            queryTransactions.isLoading === false &&
            transactionsList.length === 0 &&
            (search ? (
              <>
                No transactions found.{" "}
                <WSText.ParagraphXs
                  color="blue500"
                  inline
                  onClick={() => setFilters(defaultFilters)}
                >
                  Show full transaction history.
                </WSText.ParagraphXs>
              </>
            ) : pendingTransactions?.length ? null : (
              "No transactions found."
            ))
          }
        />
      </WSInfiniteScroll>

      <Switch>
        <Route
          path="/member/wallet/dashboard/transactions/:transactionId"
          exact
          component={WalletTransaction}
        />
      </Switch>
    </>
  );
};
